import React from "react";

import { cx } from "~/tools/class-variance";

import { useIsDropdownWithHostOpen } from "../../dropdown-with-host";
import { Menu, type MenuClickParams, MenuDivider, MenuItem } from "../menu/Menu";

import styles from "./_ctx-menu.module.css";
import { CtxMenuItem, type CtxMenuItemProps } from "./CtxMenuItem";

export type CtxMenuLayout = "vertical";

export interface CtxMenuProps {
	layout?: CtxMenuLayout;
	onClick?: VoidFunction; // this one is passed internally by antd and responsible for closing a dropdown
	className?: string;
}

export const CtxMenu: React.FC<React.PropsWithChildren<CtxMenuProps>> = ({
	children,
	layout = "vertical",
	onClick,
	className,
	...restProps
}) => {
	const isParentDropdownOpen = useIsDropdownWithHostOpen();

	const handleItemClick = ({ item, domEvent }: MenuClickParams): void => {
		isParentDropdownOpen?.write(false);
		onClick?.();
		(item?.props?.["data-menuitemprops"] as CtxMenuItemProps | undefined)?.onClick?.(domEvent as React.MouseEvent);
	};

	const renderMenuItems = (children: React.ReactNode): Array<React.ReactNode> =>
		React.Children.toArray(children)
			.filter(React.isValidElement)
			.map(
				(
					child: React.ReactElement<{
						children?: React.ReactNode;
						disabled?: boolean;
						label: string;
					}>,
				) => {
					const key = child.key === null ? undefined : child.key;

					switch (child.type) {
						case MenuItem:
							return child;

						case CtxMenuItem:
							return (
								<MenuItem
									children={child}
									data-menuitemprops={child.props}
									data-testid={`${child.props.label}`}
									disabled={child.props.disabled}
									key={key}
								/>
							);

						case MenuDivider: {
							return child;
						}

						case React.Fragment: {
							return renderMenuItems(child.props.children);
						}

						default:
							return null;
					}
				},
			);

	return (
		<Menu className={cx(styles.contextMenu, className)} mode={layout} onClick={handleItemClick} {...restProps}>
			{renderMenuItems(children)}
		</Menu>
	);
};
